import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns';
import { es } from 'date-fns/locale';

export interface MonthlyRevenueData {
  month: string;
  revenue: number;
  invoices: number;
  payments: number;
}

export interface InvoiceStatusData {
  status: string;
  count: number;
  amount: number;
  percentage: number;
}

export interface PaymentMethodData {
  method: string;
  count: number;
  amount: number;
  percentage: number;
}

export interface ActivityRevenueData {
  activity: string;
  revenue: number;
  invoices: number;
  averagePerSession: number;
}

export interface FinancialSummary {
  totalRevenue: number;
  totalInvoices: number;
  paidAmount: number;
  pendingAmount: number;
  overdueAmount: number;
  paymentRate: number;
  averageInvoiceValue: number;
  monthlyGrowth: number;
}

export const useFinancialCharts = () => {
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenueData[]>([]);
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatusData[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodData[]>([]);
  const [activityRevenue, setActivityRevenue] = useState<ActivityRevenueData[]>([]);
  const [summary, setSummary] = useState<FinancialSummary>({
    totalRevenue: 0,
    totalInvoices: 0,
    paidAmount: 0,
    pendingAmount: 0,
    overdueAmount: 0,
    paymentRate: 0,
    averageInvoiceValue: 0,
    monthlyGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener datos de los últimos 12 meses
      const endDate = new Date();
      const startDate = startOfYear(subMonths(endDate, 11));

      // Cargar datos de facturación
      const { data: billingData, error: billingError } = await supabase
        .from('student_billing')
        .select(`
          *,
          student:students(name, class),
          activity:activities(name),
          billing_period:billing_periods(name, start_date, end_date)
        `)
        .gte('invoice_date', format(startDate, 'yyyy-MM-dd'))
        .lte('invoice_date', format(endDate, 'yyyy-MM-dd'))
        .order('invoice_date', { ascending: false });

      if (billingError) throw billingError;

      // Cargar datos de transacciones de pago
      const { data: transactionData, error: transactionError } = await supabase
        .from('payment_transactions')
        .select(`
          *,
          payment_method_details:payment_methods(name)
        `)
        .gte('transaction_date', format(startDate, 'yyyy-MM-dd'))
        .lte('transaction_date', format(endDate, 'yyyy-MM-dd'))
        .order('transaction_date', { ascending: false });

      if (transactionError) throw transactionError;

      // Procesar datos para gráficos
      await processFinancialData(billingData || [], transactionData || []);

    } catch (err: any) {
      
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processFinancialData = async (
    billingData: any[],
    transactionData: any[]
  ) => {
    // 1. Datos de ingresos mensuales
    const monthlyData = new Map<string, MonthlyRevenueData>();
    
    // Inicializar últimos 12 meses
    for (let i = 11; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthKey = format(date, 'yyyy-MM');
      const monthLabel = format(date, 'MMM yyyy', { locale: es });
      
      monthlyData.set(monthKey, {
        month: monthLabel,
        revenue: 0,
        invoices: 0,
        payments: 0
      });
    }

    // Procesar facturas
    billingData.forEach(bill => {
      const monthKey = format(new Date(bill.invoice_date), 'yyyy-MM');
      const monthData = monthlyData.get(monthKey);
      
      if (monthData) {
        monthData.revenue += bill.total_amount;
        monthData.invoices += 1;
      }
    });

    // Procesar pagos
    transactionData.forEach(transaction => {
      const monthKey = format(new Date(transaction.transaction_date), 'yyyy-MM');
      const monthData = monthlyData.get(monthKey);
      
      if (monthData) {
        monthData.payments += transaction.amount;
      }
    });

    setMonthlyRevenue(Array.from(monthlyData.values()));

    // 2. Estados de facturas
    const statusMap = new Map<string, { count: number; amount: number }>();
    let totalAmount = 0;
    let totalCount = 0;

    billingData.forEach(bill => {
      const status = bill.status;
      const existing = statusMap.get(status) || { count: 0, amount: 0 };
      existing.count += 1;
      existing.amount += bill.total_amount;
      statusMap.set(status, existing);
      
      totalAmount += bill.total_amount;
      totalCount += 1;
    });

    const statusData: InvoiceStatusData[] = [];
    const statusLabels = {
      paid: 'Pagado',
      pending: 'Pendiente',
      overdue: 'Vencido',
      cancelled: 'Cancelado'
    };

    statusMap.forEach((value, key) => {
      statusData.push({
        status: statusLabels[key as keyof typeof statusLabels] || key,
        count: value.count,
        amount: value.amount,
        percentage: totalAmount > 0 ? (value.amount / totalAmount) * 100 : 0
      });
    });

    setInvoiceStatus(statusData);

    // 3. Métodos de pago
    const methodMap = new Map<string, { count: number; amount: number }>();
    let totalPayments = 0;

    transactionData.forEach(transaction => {
      const method = transaction.payment_method_details?.name || transaction.payment_method;
      const existing = methodMap.get(method) || { count: 0, amount: 0 };
      existing.count += 1;
      existing.amount += transaction.amount;
      methodMap.set(method, existing);
      
      totalPayments += transaction.amount;
    });

    const methodData: PaymentMethodData[] = [];
    methodMap.forEach((value, key) => {
      methodData.push({
        method: key,
        count: value.count,
        amount: value.amount,
        percentage: totalPayments > 0 ? (value.amount / totalPayments) * 100 : 0
      });
    });

    setPaymentMethods(methodData.sort((a, b) => b.amount - a.amount));

    // 4. Ingresos por actividad
    const activityMap = new Map<string, { revenue: number; invoices: number; sessions: number }>();
    
    billingData.forEach(bill => {
      const activity = bill.activity?.name || 'Actividad desconocida';
      const existing = activityMap.get(activity) || { revenue: 0, invoices: 0, sessions: 0 };
      existing.revenue += bill.total_amount;
      existing.invoices += 1;
      existing.sessions += bill.sessions_attended;
      activityMap.set(activity, existing);
    });

    const activityData: ActivityRevenueData[] = [];
    activityMap.forEach((value, key) => {
      activityData.push({
        activity: key,
        revenue: value.revenue,
        invoices: value.invoices,
        averagePerSession: value.sessions > 0 ? value.revenue / value.sessions : 0
      });
    });

    setActivityRevenue(activityData.sort((a, b) => b.revenue - a.revenue));

    // 5. Resumen financiero
    const paidBills = billingData.filter(bill => bill.status === 'paid');
    const pendingBills = billingData.filter(bill => bill.status === 'pending');
    const overdueBills = billingData.filter(bill => bill.status === 'overdue');
    
    const paidAmount = paidBills.reduce((sum, bill) => sum + bill.total_amount, 0);
    const pendingAmount = pendingBills.reduce((sum, bill) => sum + bill.total_amount, 0);
    const overdueAmount = overdueBills.reduce((sum, bill) => sum + bill.total_amount, 0);

    // Calcular crecimiento mensual
    const currentMonth = monthlyData.get(format(new Date(), 'yyyy-MM'));
    const previousMonth = monthlyData.get(format(subMonths(new Date(), 1), 'yyyy-MM'));
    const monthlyGrowth = (currentMonth && previousMonth && previousMonth.revenue > 0) 
      ? ((currentMonth.revenue - previousMonth.revenue) / previousMonth.revenue) * 100 
      : 0;

    setSummary({
      totalRevenue: totalAmount,
      totalInvoices: totalCount,
      paidAmount,
      pendingAmount,
      overdueAmount,
      paymentRate: totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0,
      averageInvoiceValue: totalCount > 0 ? totalAmount / totalCount : 0,
      monthlyGrowth
    });
  };

  useEffect(() => {
    loadFinancialData();
  }, []);

  return {
    monthlyRevenue,
    invoiceStatus,
    paymentMethods,
    activityRevenue,
    summary,
    loading,
    error,
    refetch: loadFinancialData
  };
};